/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */
import { IPersonaProps, PersonaInitialsColor } from '@fluentui/react/lib/Persona';
import { ChannelRosterState } from '../channelExperience/ChannelRosterState';
import { LogCategory } from '../logger';

import { ApprovalPersonaProps } from '../models/ApprovalPersonaProps';
import { getThemedIcon, IconName } from '../resources/icons/Icons';
import { ExpandValue, ServiceException, TeamTagClient, TeamTagResult } from '../service/gen';
import { PeopleResult } from '../service/gen/templates';
import {
    filterOutAlreadySelectedPeople,
    getPeopleResultsForQuery,
    getPeopleResultsForQueryInTeam,
    getTenantUsers,
    mapPeopleResultsToPersonas,
    normalizeQueryInput,
    sortByText,
} from './PeoplePickerUtils';
import { getAppHost } from './TeamsUtils';
import * as Utils from './Utils';

const teamtagClient = new TeamTagClient();

interface ITeamTagRequestContext {
    teamId?: string;
    channelId?: string;
}

const isHostTeamTagsUnavailableError = (error: unknown): boolean => {
    if (typeof error !== 'object' || error === null) {
        return false;
    }

    if (!ServiceException.isServiceException(error) || error.status !== 403) {
        return false;
    }

    try {
        const body = JSON.parse(error.response) as {
            ErrorCode?: unknown;
        };
        return body.ErrorCode === 'HostTeamTagsUnavailable';
    } catch {
        return false;
    }
};

const isTagDenied = (rosterState?: ChannelRosterState): boolean =>
    rosterState?.getSnapshot().tagAvailability === 'denied';

const resolveTeamTagRequestContext = (teamId?: string, rosterState?: ChannelRosterState): ITeamTagRequestContext => {
    const useChannelContext = rosterState !== undefined && rosterState.getSnapshot().compatibility !== 'legacyFallback';

    return {
        teamId: useChannelContext ? rosterState.context.ownerGroupId : teamId,
        channelId: useChannelContext ? rosterState.context.channelId : undefined,
    };
};

const areTeamTagsEnabled = (): boolean => {
    const config = getAppHost().getEcsConfig().MicrosoftTeamApprovals;
    return config.OnlineWorkflowSchemaVersion !== 'v1.0' && config.TeamTagEnabled;
};

/**
 * Get Personas from server side. No filtering or sorting.
 *
 * @param currentPersonas - current personas selected in a list of personas
 * @param people - list of personas
 * @param filterText - text used to filter results
 * @param teamId - team id of a Teams team
 * @return People Results.
 */
export const getPeopleAndTagResultsForQueryInPersonalApp = async (
    people: PeopleResult[],
    filterText: string,
    currentPersonas: IPersonaProps[],
    teamId?: string
): Promise<IPersonaProps[]> => {
    const logger = getAppHost().getLogger();
    let peopleList = people || [];

    // generaly, filter text should not be null or undefined, but if it is, let's make it the empty string
    if (filterText == null || filterText === undefined) {
        throw new Error('Unexpected value.');
    }

    // Lowercase the user input and strip leading and following spaces
    filterText = filterText.toLowerCase().trim();
    if (teamId) {
        try {
            const [peopleResults, teamTagResults] = await Promise.all([
                getTenantUsers(filterText),
                getFilteredTeamTags(filterText, teamId),
            ]);
            const peoplePersonnas = sortByText(mapPeopleResultsToPersonas(peopleList.concat(peopleResults)));
            const teamTagPersonnas = sortByText(mapTeamTagResultsToPersonas(teamTagResults));
            let suggestedPersonas = peoplePersonnas.concat(teamTagPersonnas);
            suggestedPersonas = filterOutAlreadySelectedPeople(suggestedPersonas, currentPersonas);
            return suggestedPersonas;
        } catch (error) {
            logger.logTraceError(
                LogCategory.PeoplePicker,
                `Failed to getPeopleAndTagResultsForQueryInPersonalApp: ${error}`
            );
            return [];
        }
    }

    // Fetch results from server in global context
    try {
        let searchList = await getTenantUsers(filterText);
        searchList = Utils.removeDuplicates(
            searchList,
            people,
            (a: PeopleResult, b: PeopleResult): boolean => a.UserAadObjectId === b.UserAadObjectId
        );
        peopleList = peopleList.concat(searchList);
    } catch (ex) {
        // do nothing, this lets us test by catching a TokenNotInitialized error
    }

    let suggestedPersonas = mapPeopleResultsToPersonas(peopleList);
    suggestedPersonas = filterOutAlreadySelectedPeople(suggestedPersonas, currentPersonas);
    return sortByText(suggestedPersonas);
};

/**
 * Get Personas in specific team.
 *
 * @param filterText - text used to filter results
 * @param teamId - team id of a Teams team
 * @param currentPersonas - current personas selected in a list of personas
 * @return ApprovalPersonaProps list
 */
export const getPeopleResultsandTeamTagResultsForQueryInTeam = async (
    filterText: string,
    teamId: string,
    currentPersonas: ApprovalPersonaProps[]
): Promise<ApprovalPersonaProps[]> => {
    const logger = getAppHost().getLogger();
    try {
        const [peoplePersonnas, teamTagResults] = await Promise.all([
            getPeopleResultsForQueryInTeam(filterText, teamId, []),
            getFilteredTeamTags(filterText, teamId),
        ]);
        const teamTagPersonnas = sortByText(mapTeamTagResultsToPersonas(teamTagResults));
        let suggestedPersonas = peoplePersonnas.concat(teamTagPersonnas);
        suggestedPersonas = filterOutAlreadySelectedPeople(suggestedPersonas, currentPersonas);
        return suggestedPersonas;
    } catch (error) {
        logger.logTraceError(
            LogCategory.PeoplePicker,
            `Failed to getPeopleResultsandTeamTagResultsForQueryInTeam: ${error}`
        );
        return [];
    }
};

/**
 * Convert list of TeamTags to list of IPersonaProps
 *
 * @param teamTagList - list of TeamTagResult
 * @return PersonaPropsList
 **/
const mapTeamTagResultsToPersonas = (teamTagList: TeamTagResult[]): IPersonaProps[] => {
    const loc = getAppHost().getLocale();
    const teamTagIcon = getThemedIcon(IconName.teamTagIcon);
    return teamTagList.map((tag: TeamTagResult): IPersonaProps => {
        const result: ApprovalPersonaProps = {
            text: tag.DisplayName,
            secondaryText: Utils.stringFormat(loc.strings.teamtag_members_description, {
                totalNumber: `${tag.MemberCount}`,
            }),
            id: tag.Id,
            onRenderInitials: (): JSX.Element => teamTagIcon,
            initialsColor: PersonaInitialsColor.transparent,
            coinSize: getAppHost().isMobile() ? 36 : 30,
            isTeamTag: true,
            teamTagMemberCount: tag.MemberCount,
            teamId: tag.TeamId,
        };
        return result;
    });
};

/**
 * Get the filtered tags for the team context.
 *
 * @param queryString Input string used to filter users
 * @param teamId Team ID for the group context.
 * @return People Results.
 */
export const getFilteredTeamTags = async (
    queryString: string,
    teamId?: string,
    rosterState?: ChannelRosterState
): Promise<TeamTagResult[]> => {
    const normalizedInput = normalizeQueryInput(queryString);
    if (!normalizedInput || !areTeamTagsEnabled() || isTagDenied(rosterState)) {
        return [];
    }

    const requestContext = resolveTeamTagRequestContext(teamId, rosterState);
    if (!requestContext.teamId) {
        rosterState?.setTagAvailability('unavailable');
        return [];
    }

    const request = requestContext.channelId
        ? {
              QueryText: normalizedInput,
              CurrentChannelId: requestContext.channelId,
          }
        : {
              QueryText: normalizedInput,
          };

    try {
        const teamTagResults = await teamtagClient.searchTeamTagResults(requestContext.teamId, request);
        if (isTagDenied(rosterState)) {
            return [];
        }
        rosterState?.setTagAvailability('available');
        return teamTagResults;
    } catch (error) {
        if (isHostTeamTagsUnavailableError(error)) {
            rosterState?.setTagAvailability('denied');
            return [];
        }
        if (!rosterState) {
            throw error;
        }
        rosterState.setTagAvailability('unavailable');
        return [];
    }
};

/**
 * Get the filtered tags for the team context.
 *
 * @param teamId Team ID for the group context.
 * @param tagId Tag ID for a Teams Tag.
 * @param expand the value will descide if the team tag results contain tag members or tag members with photo.
 * @param rosterState Channel-scoped roster state when resolving tags during approval creation.
 * @return TeamTag Result.
 */
export const getTeamTag = async (
    teamId?: string,
    tagId?: string,
    expand?: ExpandValue,
    rosterState?: ChannelRosterState
): Promise<TeamTagResult | undefined> => {
    const requestContext = resolveTeamTagRequestContext(teamId, rosterState);

    if (!requestContext.teamId || !tagId || isTagDenied(rosterState)) {
        return;
    }

    try {
        const result = await teamtagClient.getTeamTagDetail(
            requestContext.teamId,
            tagId,
            expand,
            requestContext.channelId
        );
        if (isTagDenied(rosterState)) {
            return;
        }
        rosterState?.setTagAvailability('available');
        return result;
    } catch (error) {
        if (isHostTeamTagsUnavailableError(error)) {
            rosterState?.setTagAvailability('denied');
        }
        throw error;
    }
};

/**
 * Get the filtered tags for the team context.
 *
 * @param teamId Team ID for the group context.
 * @param tagId Tag ID for a Teams Tag.
 * @param expand the value will descide if the team tag results contain tag members or tag members with photo.
 * @return TeamTag Results.
 */
export const batchGetTeamTag = async (
    teamId: string,
    tagIds: string[],
    expand?: ExpandValue
): Promise<TeamTagResult[] | undefined> => {
    if (!teamId || !tagIds?.length) {
        return;
    }
    // Batch reads hydrate existing approval details, which do not carry channel context.
    const requests = tagIds.map(
        (tId: string): Promise<TeamTagResult> => teamtagClient.getTeamTagDetail(teamId, tId, expand, undefined)
    );
    return Promise.all(requests);
};

/**
 * Get Personas from Local 'conversation' roster cache
 *
 * @param currentPersonas - current personas selected in a list of personas
 * @param people - list of personas
 * @param filterText - text used to filter results
 * @param teamId - team id of a Teams team
 * @return Persona List
 */
export const getPeopleResultsAndTagResultsForQuery = async (
    people: PeopleResult[],
    filterText: string,
    currentPersonas: IPersonaProps[],
    teamId?: string,
    onChannelSearched?: (searched: boolean) => void,
    rosterState?: ChannelRosterState
): Promise<IPersonaProps[]> => {
    try {
        const [peoplePersonnas, teamTagResults] = await Promise.all([
            getPeopleResultsForQuery(people, filterText, [], teamId, onChannelSearched, rosterState),
            getFilteredTeamTags(filterText, teamId, rosterState),
        ]);
        const visibleTagResults = rosterState?.getSnapshot().tagAvailability === 'denied' ? [] : teamTagResults;
        const teamTagPersonnas = sortByText(mapTeamTagResultsToPersonas(visibleTagResults));
        let suggestedPersonas = peoplePersonnas.concat(teamTagPersonnas);
        suggestedPersonas = filterOutAlreadySelectedPeople(suggestedPersonas, currentPersonas);
        return suggestedPersonas;
    } catch (ex) {
        return currentPersonas;
    }
};
